import { h } from 'preact';
import { formatMoney } from 'utils/storefront/format-resource';
import { useMemo, useEffect, useState, useRef, useReducer } from 'preact/hooks';
import Storefront from 'utils/storefront';
import Image from "utils/image";
import { withCartContext } from './cart-context';
import CartItemPrice from './cart-item-price';
import Icon from '../utils/icon';
import { productCardGet } from '../utils/storefront/queries';
import Gift from './gift';
import AjaxServiceSingleton from './ajax-service';

const CartItem = ({
  cart,
  line,
  giftWrappingProduct,
  preventedPurchaseItem
}) => {
  const maxQuantity = line?.quantityAvailable || 100;
  const optionsWithValues = line.options_with_values;
  const colorOption = optionsWithValues.find(option => option.name === 'Color');
  const colorValue = colorOption ? colorOption.value : null
  const [lineVariant, setLineVariant] = useState('');
  const [productTags, setProductTags] = useState('');
  const [activeProduct, setActiveProduct] = useState();
  const [isInStock, setInStock] = useState(true);
  const [isLowStock, setLowStock] = useState(false);
  const storefront = Storefront.getInstance();
  const [quantityUpdate,setQuantityUpdate] = useState('')
  const isPreventedItem = preventedPurchaseItem?.id
    === `gid://shopify/ProductVariant/${line.id}`;
  const forceUpdate = useReducer(() => ({}), {})[1];

  const findVariantById = (variants, id) => {
    const gid = `gid://shopify/ProductVariant/${id}`
    return variants?.find(variant => variant.node.id === gid)?.node || null;
  };

  const findGiftWrappingProduct = () => {
    const foundItem = cart.state.items.find(item =>
      giftWrappingProduct?.id.includes(item.variant_id)
    );

    if (foundItem) {
      return foundItem;
    }
    return null;
  }

  const findEngravingProduct = () => {
    const foundEngravingItem = cart.state.items.find(item =>
      item.properties?._EngravingProduct
    );
    const foundEmbossingItem = cart.state.items.find(item =>
      item.properties?._EmbossingProduct
    );
    const item = foundEngravingItem || foundEmbossingItem;
    return item
  }

  const giftWrapping = findGiftWrappingProduct();
  const engravingProduct = findEngravingProduct();
  const engravingProductPrice = engravingProduct?.price ?
    engravingProduct.price / 100 : null;
    
  const getLineProductData = async () => {
    const lineProductGid = `gid://shopify/Product/${line.product_id}`
    const response = await storefront.request({
      query: productCardGet,
      variables: {
        id: lineProductGid
      },
    });

    setProductTags(response?.data?.product?.tags)

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'cart-item.js: Storefront API request failed',
        response,
      );
      return [];
    }

    return response.data;
  };

  const processLineProductData = (lineProductData) => {
    const variantData = findVariantById(
      lineProductData.product?.variants?.edges,
      line.id
    );

    setLineVariant(variantData);
  };

  const handleCurrentVariantLowStock = (e) => {
    if (line.variant_id === e.detail.payload.id) {
      setInStock(true);
      setLowStock(true);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (line && line.variant_id) {
        try {
          const lineProductData = await getLineProductData();
          setActiveProduct(lineProductData.product);
          processLineProductData(lineProductData);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('cart-item.js: Storefront API request failed', error);
        }
      }
    };

    fetchData();
  }, [line.variant_id]);

  useEffect(() => {
    document.addEventListener("variant:low-stock", handleCurrentVariantLowStock);

    return () => {
      document.removeEventListener("variant:low-stock", handleCurrentVariantLowStock);
    }
  }, []);

  const handleAdjustQuantity = async (quantity, isRemoveTotally = false) => {
    const qty = quantity > maxQuantity ? maxQuantity : quantity;
    let payload = [];
  
    // Gift wrapping sync logic
    if (giftWrapping && line.properties?._gift_wrapping === true) {
      let newGiftQuantity = giftWrapping.quantity;
  
      if (isRemoveTotally && line.quantity === newGiftQuantity) {
        newGiftQuantity = 0;
      } else if (isRemoveTotally && line.quantity > 1) {
        newGiftQuantity -= line.quantity;
      } else if (qty > line.quantity) {
        newGiftQuantity += 1;
      } else {
        newGiftQuantity -= 1;
      }
  
      payload.push(
        {
          id: line.key,
          quantity: qty,
        },
        {
          id: giftWrapping.key,
          quantity: newGiftQuantity,
        }
      );
    }
  
    // Engraving sync logic
    const currentEngravingProduct = cart.state.items.find(item =>
      item.properties?._EngravingProduct
    );
  
    if (currentEngravingProduct && line.properties?.Engraving) {
      let newEngravingQuantity = currentEngravingProduct.quantity;
  
      if (isRemoveTotally && line.quantity === newEngravingQuantity) {
        newEngravingQuantity = 0;
      } else if (isRemoveTotally && line.quantity > 1) {
        newEngravingQuantity -= line.quantity;
      } else if (qty > line.quantity) {
        newEngravingQuantity += 1;
      } else {
        newEngravingQuantity -= 1;
      }
  
      payload.push(
        {
          id: line.key,
          quantity: qty,
        },
        {
          id: currentEngravingProduct.key,
          quantity: newEngravingQuantity,
        }
      );
    }
  
    // Embossing sync logic
    const embossingProduct = cart.state.items.find(item =>
      item.properties?._EmbossingProduct
    );
  
    if (embossingProduct && line.properties?.Embossing) {
      let newEmbossingQuantity = embossingProduct.quantity;
  
      if (isRemoveTotally && line.quantity === newEmbossingQuantity) {
        newEmbossingQuantity = 0;
      } else if (isRemoveTotally && line.quantity > 1) {
        newEmbossingQuantity -= line.quantity;
      } else if (qty > line.quantity) {
        newEmbossingQuantity += 1;
      } else {
        newEmbossingQuantity -= 1;
      }
  
      payload.push(
        {
          id: line.key,
          quantity: qty,
        },
        {
          id: embossingProduct.key,
          quantity: newEmbossingQuantity,
        }
      );
    }
  
    // Fallback (no extras)
    if (payload.length === 0) {
      payload.push({
        id: line.key,
        currentQty: line.quantity,
        quantity: qty,
      });
    }
  
    // Handle bundled items
    if (line.children) {
      payload = line.children.map((child) => ({
        id: child.id,
        attributes: child.attributes,
        quantity: qty,
      }));
    }
  
    try {
      const result = await cart.update(payload);
      return result;
    } catch (e) {
      if (e.message === "Out of stock") {
        setInStock(false);
      }
    }
  
    return true;
  };
  

  const handleRemove = () => {
    handleAdjustQuantity(0,true);
  };

  const GiftComponent = () => {
    if (!giftWrappingProduct.enableGiftWrapping ||
      !giftWrappingProduct.id) {
      return null;
    }
    if (
      activeProduct?.allowGiftMessage?.value !== '1' &&
      activeProduct?.allowGiftWrap?.value !== '1'
    )
      return null;
    const divRef = useRef(null);

    return (
      <button className='pl-1.5 pr-2.5 bg-grey-7 h-fit py-2 flex justify-between items-center cursor-pointer' 
        ref={divRef} 
        aria-label={window.TMW.Strings.cart.giftOptions}
      >
        <div className='flex flex-col gap-2 w-full'>
          <div className='flex items-center gap-1'>
            <span>
              <Icon name='gift' />
            </span>
            <p className='utility-4'>
              {window.TMW.Strings.cart.giftOptions}
            </p>
          </div>

          {(line.properties["Gift Message"] ||
            line.properties?._gift_wrapping) &&
            <ul className='flex flex-col text-grey-8 gap-2 list-disc list-inside truncate'>
              {line.properties["Gift Message"] &&
                <li className=
                  'paragraph-6 text-grey-8 truncate max-w-[150px]'
                >
                  {line.properties["Gift Message"]}
                </li>
              }
              {line.properties?._gift_wrapping &&
                <li className='paragraph-6 text-grey-8'>
                  {giftWrappingProduct.title} + {formatMoney({
                    amount: giftWrappingProduct.amount,
                    currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                  })}
                </li>
              }
            </ul>
          }
          <Gift
            activeProduct={activeProduct}
            openEl={divRef}
            cart={cart}
            line={line}
            giftWrappingProduct={giftWrappingProduct}
            giftWrappingQuantity={giftWrapping?.quantity}
          />
        </div>
        <div className='border-l border-grey-3 flex items-center justify-center h-full pl-2.5'>
          <Icon name='carret' />
        </div>
      </button>
    );
  }

  const itemIndex = cart.state.items.findIndex(item => item.id === line.id) + 1;
 
  const checkAndRemoveUnavailableItems = async (lineItem) => {
    try {
      const lineProductData = await getLineProductData();
      
      if (!lineProductData?.product?.variants?.edges) {
        console.error('Failed to retrieve product variant data');
        return false;
      }
  
      const variant = lineProductData.product.variants.edges.find(
        v => v.node.id === `gid://shopify/ProductVariant/${lineItem.id}`
      )?.node;
  
      // Check if product is digital gift card (by handle or by recipient properties)
      const isDigitalGiftCard = lineProductData.product.handle?.includes('digital-gift-card') ||
        lineItem.properties?.['Recipient email address'];
      
      if (variant && variant.quantityAvailable <= 0 && 
          lineProductData.product.handle !== 'engraving' && 
          lineProductData.product.handle !== 'embossing' && 
          lineProductData.product.handle !== 'gift-wrapping' &&
          !isDigitalGiftCard) {
        try {
          // 1. Remove the item using the cart's remove method if available
          if (cart.remove) {
            await cart.remove([lineItem.id]);
          } 
          // Fallback to AjaxService
          else {
            await AjaxServiceSingleton.remove(null, [lineItem.id]);
          }
  
          // 2. Force a full cart refresh
          if (cart.update) {
            await cart.update();
          }
  
          // 3. Force a re-render
          if (forceUpdate) {
            forceUpdate();
          }
  
          return true;
        } catch (error) {
          console.error('Error removing item from cart:', error);
          // If there was an error, try to refresh the cart to ensure consistency
          if (cart.update) {
            await cart.update();
          }
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking product availability:', error);
      return false;
    }
  };
  useEffect(() => {
    const checkAndUpdate = async () => {
      try {
        await checkAndRemoveUnavailableItems(line);
      } catch (error) {
        console.error('Error in checkAndRemoveUnavailableItems:', error);
      }
    };
    checkAndUpdate();
  }, [line]);


  const lineItem = useMemo(
    () => (
      <div
        className='relative flex flex-col gap-1 md:gap-2'
        role="group"
        aria-roledescription="product"
        aria-label={`Product item ${itemIndex}  of ${cart.state.items.length}`}
        key={line.id}
      >
        <div className="flex gap-1.5 basis-0 border-b border-grey-3 pb-6">
          <Image
            classes="w-[153px] min-w-[153px] h-[153px] aspect-square"
            src={line.image}
            srcWidths={{ sm: 200, md: 300, lg: 300, xl: 300, '2xl': 300 }}
            width={line.featured_image.width}
            height={line.featured_image.height}
            alt={line.featured_image.altText}
          />
          <div className="flex flex-col justify-between gap-2.5 w-full">
            <div className="flex flex-col gap-1.5">
              <h3 className="paragraph-4">
                <a className="no-underline text-primary paragraph-4"
                  tabIndex="0"
                  href={
                    !line.properties?._GiftProduct &&
                    !line.properties?._EngravingProduct &&
                    line.url}
                    aria-label={line.product_title}>
                  {line.product_title}
                </a>
              </h3>
              {colorValue &&
                <p className='paragraph-5 text-grey-8'>
                  {colorValue}
                </p>
              }
              <CartItemPrice
                quantity={line.quantity}
                price={lineVariant.price}
                discountedPrice={line.discounted_price / 100}
                compareAtPrice={lineVariant.compareAtPrice}
                productTags={productTags}
              />
              {
                line.properties &&
                <div className='flex flex-col gap-1'>
                  {
                    Object.entries(line.properties).map(([key, value]) => (
                      key === 'Engraving' || key === 'Embossing' ? (
                        <div className='flex gap-1' key={key}>
                          <p className='paragraph-6 text-grey-8 max-w-[115px]'>
                            {key}: {value} + {formatMoney({
                            amount: engravingProductPrice * line.quantity,
                            currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                          })}
                          </p>
                          <div className="flex items-center gap-1">
                          <span
                            className="inline-block w-1 h-1 mt-0.5 rounded-full bg-red-500 flex-shrink-0 flex-grow-0" />
                            <p className="paragraph-6 text-error">
                              {window.TMW.Strings.cart.finalSale}
                            </p>
                          </div>
                        </div>
                      ) : (!key.startsWith('_') && !key.includes('Gift')) ? (
                        <p key={key} className='paragraph-6 text-grey-8 break-all'>
                          {key}: {value}
                        </p>
                      ) : null
                    ))
                  }
                </div>
              }
            </div>
            <GiftComponent />
            <div className='flex justify-between items-end'>
              <div className='w-1/2'>
                <div className="border border-grey-3 p-3 flex items-center gap-2 utility-3 text-grey-8 max-h-8">
                  {
                    !line.properties?._GiftProduct &&
                    !line.properties?._EngravingProduct &&
                    <button
                      className="w-3"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleAdjustQuantity(line.quantity - 1);
                          setQuantityUpdate(`Quantity decreased 
                            to ${line.quantity-1}`)
                          e.target.focus();
                        }
                      }}
                      onClick={() => {handleAdjustQuantity(line.quantity - 1);
                        setQuantityUpdate(`Quantity decreased 
                          to ${line.quantity-1}`)}} 
                    
                      aria-label={window.TMW.Strings.cart.decrease}
                    >
                      <span className='sr-only'>
                        {window.TMW.Strings.cart.decrease}
                      </span>
                      &#8722;
                    </button>
                  }
                  <span className='sr-only' aria-live="assertive">{quantityUpdate}</span>
                  <input
                    type="number"
                    value={line.quantity}
                    className="input-number p-0 border-0 w-full text-center utility-3"
                    aria-label="quantity"
                    max={maxQuantity}
                    disabled
                  />
                  {
                    !line.properties?._GiftProduct &&
                    !line.properties?._EngravingProduct &&
                    <button
                      className={
                        `w-3 -mt-0.5 
                        ${line.quantity >= maxQuantity ? 'text-grey-2' : ''}`
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleAdjustQuantity(line.quantity + 1);
                          setQuantityUpdate(`Quantity increased 
                            to ${line.quantity+ 1}`)
                          e.target.focus();
                        }
                      }}
                      onClick={() => {handleAdjustQuantity(line.quantity + 1); 
                        setQuantityUpdate(`Quantity increased 
                          to ${line.quantity+ 1}`)}}
                
                      aria-label={window.TMW.Strings.cart.increase}
                      disabled={line.quantity >= maxQuantity || !isInStock}
                    >
                      &#43;
                      <span className='sr-only'>
                        {window.TMW.Strings.cart.increase}
                      </span>
                    </button>
                  }
                </div>
              </div>
              {
                !line.properties?._GiftProduct &&
                !line.properties?._EngravingProduct &&
                <button
                  type="button"
                  className="body-2 md:body-1 underline paragraph-6 text-grey-8"
                  onClick={() => handleRemove()}>
                  {window.TMW.Strings.cart.remove}
                </button>
              }
            </div>
            {
              line.properties &&
              <div className='flex flex-col gap-1'>
                {isPreventedItem && (
                  <div className="flex items-start gap-1">
                    <span
                      className="inline-block w-1 h-1 mt-1.5 rounded-full bg-red-500 flex-shrink-0 flex-grow-0" />
                    <p className="paragraph-6 text-error">
                      {window.TMW.Strings.product.preventPurchase.replace('{{ product_title }}', `${preventedPurchaseItem?.product?.title} - ${preventedPurchaseItem?.selectedOptions[0]?.value}`)}
                    </p>
                  </div>
                )}
              </div>
            }
            {isInStock && isLowStock && (
              <div className="flex items-center">
                <span className="inline-block w-[5px] h-[5px] rounded-full bg-red-500 mr-[5px] mt-[2px]" />
                <span className="text-red-500 font-light paragraph-4">{window.TMW.Strings.product.lowStock}</span>
              </div>
            )}

            {!isInStock && (
              <div className="flex">
                <span className="inline-block w-[7px] h-[5px] rounded-full bg-red-500 mr-[5px] mt-[6px]" />
                <span className="text-red-500 font-light paragraph-4">{window.TMW.Strings.product.outOfStockMessage}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [line, lineVariant, isInStock, isLowStock, preventedPurchaseItem],
  );
  return lineItem
};

export default withCartContext(CartItem);
